import { Link, navigate } from 'gatsby';
import React, { Fragment, forwardRef, memo, useState } from 'react';
import { mobileMenuItems } from '../../../../constants/menus';
import { ACCOUNT_ORDERS_PAGE, LOGIN_PAGE, SEARCH_PAGE, TRY_AT_HOTEL_PAGE } from '../../../../constants/page-links';
import { useScreen } from '../../../../hooks/useScreen';
import { useScrollDirection } from '../../../../hooks/useScrollDirection';
import { classNames } from '../../../../utils/classnames';
import { getURLPathname } from '../../../../utils/parseURL';
import {
  convertArrayToString,
  convertStringToLocaleLowerCase,
  convertStringToLowercase
} from '../../../../utils/convertValues';
import { isEmpty } from '../../../../utils/typeCheck';
import { SearchHeaderListItem } from '../../../lists/items/header';
import { LogoHeader } from '../logo';
import { BeddingContentDropdown } from './content-dropdown/bedding';
import { BedsContentDropdown } from './content-dropdown/beds';
import { FurnitureContentDropdown } from './content-dropdown/furniture';
import { MoreContentDropdown } from './content-dropdown/more';
import { WhyDuxContentDropdown } from './content-dropdown/why-dux';
import { useAppContext } from '../../../../contexts/AppProvider';

const MENU_ITEMS_SHOWING = ['Beds', 'Furniture', 'Bedding', 'Why Dux', 'Hotel', 'More'];
const profileText = 'Profile';
const loginText = 'Login';
export const MainHeader = memo(
  forwardRef((props, ref) => {
    const { menu = null } = props;
    // Translations
    const searchText = 'Search';
    const moreText = 'More';
    const expandCollapseMenuText = 'Expand/collapse menu';
    const { isMobile } = useScreen();
    const scrollDirection = useScrollDirection();
    const appData = useAppContext();
    const [menuId, setMenuId] = useState();
    const handleMainHeaderAriaLabel = (menu) => {
      const labels =
        menu
          ?.filter((item) => MENU_ITEMS_SHOWING.includes(item?.contentLink?.expanded?.name))
          .map((item) => convertStringToLowercase(item?.contentLink?.expanded?.name || '')) || [];

      labels.push(convertStringToLowercase('more'));

      return convertArrayToString(labels);
    };

    const collapseHeaderPanel = () => {
      ref?.current?.classList?.remove('header--expanded');
    };

    const closeMenuPanel = () => {
      // to display the content on expanded panel
      document.getElementById(menuId)?.classList?.remove('primary-navigation__item--expanded');
    };

    const onExpandedMenuClick = (e, link, useClosePanelClass = false) => {
      e.preventDefault();

      //  useClosePanelClass is used to prevent the main menu closing, "true" means when we select any menu item from mobile view for multiple times it will close the menu panel
      if (isMobile && useClosePanelClass && e?.target && ref?.current?.classList?.contains('header--expanded')) {
        setTimeout(() => {
          ref?.current?.classList?.remove('header--expanded');
        }, 200);
      }

      if (link) {
        setTimeout(() => {
          navigate(link);
        }, 500);
      }
    };
    if (isEmpty(menu)) return null;

    const getLabelAndLinkForRoute = (item) => {
      const lowerCaseLabel = convertStringToLowercase(item?.label);
      const label = lowerCaseLabel === 'login' ? (appData?.customerData ? profileText : loginText) : item?.label;
      const url = lowerCaseLabel === 'login' ? (appData?.customerData ? ACCOUNT_ORDERS_PAGE : LOGIN_PAGE) : item?.url;

      return { label, url };
    };

    return (
      <>
        <LogoHeader
          onClick={() => {
            collapseHeaderPanel();
            closeMenuPanel();
            props.setDisplayMainMenuClass(true);
          }}
        />
        <div className="header__main" aria-label={handleMainHeaderAriaLabel(menu)}>
          <nav className="primary-navigation" aria-label="main menu">
            <Link
              to={SEARCH_PAGE}
              className={
                'primary-navigation__search-trigger transition-for-scroll ' +
                (isMobile && scrollDirection === 'down' && '-top-24')
              }
              onClick={collapseHeaderPanel}
            >
              <span className="sr-only">{searchText}</span>
            </Link>
            <div
              className={
                'primary-navigation__trigger toggle-primary-dropdown transition-for-scroll ' +
                (isMobile && scrollDirection === 'down' && '-top-24')
              }
              tabIndex="0"
              aria-label={expandCollapseMenuText}
            >
              <span></span>
            </div>
            {isMobile && (
              <div className="secondary-navigation secondary-navigation--mobile">
                <ul role="list">
                  {/* here mobile menu has a static values, fetch those from the CMS  */}
                  {mobileMenuItems?.secondary?.map((item, index) => {
                    return (
                      <li
                        key={item?.url || index}
                        className={classNames(
                          'secondary-navigation__item',
                          !isEmpty(item?.label) &&
                            `secondary-navigation--${convertStringToLocaleLowerCase(item?.label)}`
                        )}
                        role="menuitem"
                        aria-haspopup="true"
                      >
                        <Link
                          to={item?.url || '#'}
                          className="toggle-dropdown"
                          role="button"
                          tabIndex="0"
                          aria-label={convertStringToLowercase(item?.label?.split(' ')?.join('-'))}
                          onClick={(e) => onExpandedMenuClick(e, getLabelAndLinkForRoute(item)?.url, true)}
                        >
                          {getLabelAndLinkForRoute(item)?.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <ul role="menu" aria-label={handleMainHeaderAriaLabel(menu)}>
              {menu?.map((item, index) => {
                if (
                  item?.contentLink?.expanded?.name &&
                  isMobile &&
                  !MENU_ITEMS_SHOWING.includes(item?.contentLink?.expanded?.name)
                ) {
                  return null;
                }

                if (convertStringToLowercase(item?.contentLink?.expanded?.name) === convertStringToLowercase('Hotel')) {
                  return (
                    <li
                      key={item?.contentLink?.expanded?.contentLink?.id || index}
                      className="primary-navigation__item"
                      role="menuitem"
                      aria-expanded="false"
                      aria-label={convertStringToLowercase(item?.contentLink?.expanded?.name?.split(' ')?.join('-'))}
                    >
                      <a
                        href="#"
                        role="button"
                        tabIndex={0}
                        aria-label={convertStringToLowercase(item?.contentLink?.expanded?.name?.split(' ')?.join('-'))}
                        onClick={(e) => {
                          onExpandedMenuClick(e, TRY_AT_HOTEL_PAGE, true);
                          collapseHeaderPanel();
                        }}
                      >
                        Try at {item?.contentLink?.expanded?.name}
                      </a>
                    </li>
                  );
                }

                return (
                  <Fragment key={item?.contentLink?.expanded?.contentLink?.id || index}>
                    <li
                      className="primary-navigation__item has-children is-dropdown"
                      role="menuitem"
                      aria-expanded="false"
                      id={item?.contentLink?.expanded?.name}
                      aria-haspopup="true"
                      aria-label={convertStringToLowercase(item?.contentLink?.expanded?.name?.split(' ')?.join('-'))}
                    >
                      <a
                        href="#"
                        className="toggle-dropdown"
                        role="button"
                        tabIndex="0"
                        aria-label={convertStringToLowercase(item?.contentLink?.expanded?.name?.split(' ')?.join('-'))}
                        onClick={(e) => {
                          onExpandedMenuClick(e);
                          setMenuId(item?.contentLink?.expanded?.name);
                        }}
                      >
                        {item?.contentLink?.expanded?.name}
                      </a>

                      <div className="primary-navigation__dropdown">
                        <div className="primary-navigation__dropdown-inner">
                          {convertStringToLowercase(item.contentLink.expanded.name) ===
                            convertStringToLowercase('Beds') && <BedsContentDropdown activeMenuContent={item} />}

                          {convertStringToLowercase(item.contentLink.expanded.name) ===
                            convertStringToLowercase('Furniture') && (
                            <FurnitureContentDropdown activeMenuContent={item} />
                          )}

                          {convertStringToLowercase(item.contentLink.expanded.name) ===
                            convertStringToLowercase('Bedding') && <BeddingContentDropdown activeMenuContent={item} />}

                          {convertStringToLowercase(item.contentLink.expanded.name) ===
                            convertStringToLowercase('Why Dux') && <WhyDuxContentDropdown activeMenuContent={item} />}
                        </div>
                      </div>
                    </li>
                  </Fragment>
                );
              })}

              <li
                className="primary-navigation__item has-children is-dropdown more-dropdown"
                role="menuitem"
                id="more"
                aria-expanded="false"
                aria-haspopup="true"
                aria-label="more"
              >
                <a
                  href="#"
                  className="toggle-dropdown"
                  role="button"
                  tabIndex="0"
                  aria-label="more"
                  onClick={(e) => {
                    onExpandedMenuClick(e);
                    setMenuId('more');
                  }}
                >
                  {moreText}
                </a>
                <div className="primary-navigation__dropdown">
                  <div className="primary-navigation__dropdown-inner">
                    <MoreContentDropdown menu={menu} />
                  </div>
                </div>
              </li>
              {/* here mobile menu has a static values, fetch those from the CMS  */}
              {isMobile && (
                <>
                  {menu
                    .filter(
                      (item) =>
                        convertStringToLowercase(item?.contentLink?.expanded?.name) !== 'beds' &&
                        convertStringToLowercase(item?.contentLink?.expanded?.name) !== 'furniture' &&
                        convertStringToLowercase(item?.contentLink?.expanded?.name) !== 'bedding' &&
                        convertStringToLowercase(item?.contentLink?.expanded?.name) !== 'hotel' &&
                        convertStringToLowercase(item?.contentLink?.expanded?.name) !== 'why dux'
                    )
                    .map((item, index) => (
                      <li
                        key={item?.contentLink?.expanded?.page?.id || index}
                        className="primary-navigation__item primary-navigation--smaller"
                        role="menuitem"
                        aria-expanded="false"
                        aria-haspopup="true"
                        aria-label={convertStringToLowercase(
                          item?.contentLink?.expanded?.page?.expanded?.name ||
                            item?.contentLink?.expanded?.page?.expanded?.metaTitle ||
                            ''
                        )}
                      >
                        <Link
                          to={
                            typeof item.contentLink?.expanded?.page?.url === 'string' &&
                            item.contentLink?.expanded?.page?.url?.length > 0
                              ? getURLPathname(item.contentLink.expanded.page.url)
                              : '#'
                          }
                          className="toggle-dropdown"
                          tabIndex="0"
                          aria-label={convertStringToLowercase(
                            item?.contentLink?.expanded?.page?.expanded?.name ||
                              item?.contentLink?.expanded?.page?.expanded?.metaTitle ||
                              ''
                          )}
                          onClick={(e) =>
                            onExpandedMenuClick(
                              e,
                              typeof item.contentLink?.expanded?.page?.url === 'string' &&
                                item.contentLink?.expanded?.page?.url?.length > 0
                                ? getURLPathname(item.contentLink.expanded.page.url)
                                : '#',
                              true
                            )
                          }
                        >
                          {item?.contentLink?.expanded?.page?.expanded?.listHeading ||
                            item?.contentLink?.expanded?.page?.expanded?.name ||
                            ''}
                        </Link>
                      </li>
                    ))}
                  <div className="mb-8"></div>
                </>
              )}

              {!isMobile && <SearchHeaderListItem hideSearchPanel={props.displayMainMenuClass} />}
            </ul>
          </nav>
        </div>
      </>
    );
  })
);
